import { FC, ChangeEvent, useEffect, useMemo, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { ErrorType, recordError } from '../../../models/apiWrappers/api/exceptions';
import { useMst } from '../../../models/rootModel';
import { ErrorInputText } from '../../styledComponent/SignUpComponents.style';
import {
  AppliedCupponTag,
  AppliedCupponTagDeleteIcon,
  AppliedCupponTagIcon,
  AppliedCupponTagText,
  ApplyButton,
  CouponInput,
  CouponInputWrapper,
  CouponWrapped,
  InputRow,
  ShowCouponInputField,
} from './CouponForm.styles';
import { useAnalytics } from '../../../utils/analytics';
import { InputColorVariant } from '../../common/InputField/InputField.types';

import greenTag from '../../../assets/greenTag.svg';
import closeIcon from '../../../assets/closeIcon.svg';
import { couponResult } from './paymentStep.types';

export const CouponForm: FC = observer(() => {
  const { UserPayment } = useMst();
  const analytics = useAnalytics();

  const searchParams = new URLSearchParams(window.location.search);
  const coupon = searchParams.get('coupon');
  const { showCouponInput, setShowCouponInput, couponCodeText, setCouponCodeText, appliedCoupon, setAppliedCoupon } =
    UserPayment.paymentComponentMetadata.couponForm;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const canCouponBeApplied = useMemo(
    () => Boolean(!isLoading && couponCodeText && !appliedCoupon),
    [isLoading, couponCodeText, appliedCoupon]
  );

  const showCouponInputClick = () => {
    if (showCouponInput) {
      return;
    }
    setShowCouponInput(true);
    analytics.track('click add coupon');
  };

  const onCouponInputValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setCouponCodeText(value);
  };

  const onApplyCoupon = async (coupon = couponCodeText) => {
    if (isLoading || !coupon) {
      return;
    }

    analytics.track('coupon apply attempt', {
      couponCode: coupon,
    });

    try {
      setError(undefined);
      setIsLoading(true);
      const result = await UserPayment.getDiscountFromPromoCode(coupon);

      if (result.valid) {
        await UserPayment.applyCustomCouponCode(coupon);
        UserPayment.setDiscount(result.discount);
        if (result.discount.coupon.percentOff) {
          setAppliedCoupon({
            discountPercentage: result.discount.coupon.percentOff,
            code: coupon,
            discountAmount: 0,
          });
          analytics.track('coupon apply attempt result', {
            couponCode: coupon,
            couponResult: couponResult.Success,
            couponDiscountAmount: result.discount.coupon.percentOff,
          });
        } else {
          setAppliedCoupon({
            discountPercentage: 0,
            code: coupon,
            discountAmount: result.discount.coupon.amountOff,
          });
          analytics.track('coupon apply attempt result', {
            couponCode: coupon,
            couponResult: couponResult.Success,
            couponDiscountAmount: result.discount.coupon.amountOff,
          });
        }
      } else {
        analytics.track('coupon apply attempt result', {
          couponCode: coupon,
          couponResult: couponResult.Invalid,
        });

        setError(result.error?._uiNotice.title ?? 'Code is invalid');
      }
    } catch (error) {
      recordError(ErrorType.HANDLED, error);
      setError('Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  const onRemoveCouponClick = () => {
    UserPayment.setDiscount(null);
    setAppliedCoupon(null);
    setShowCouponInput(true);
  };

  useEffect(() => {
    if (coupon) {
      setCouponCodeText(coupon);
      onApplyCoupon(coupon);
    }
    // eslint-disable-next-line
  }, []);

  const onApplyClick = () => {
    onApplyCoupon();
  };

  return (
    <CouponWrapped>
      {!appliedCoupon && <ShowCouponInputField onClick={showCouponInputClick}>Add coupon</ShowCouponInputField>}

      {!appliedCoupon && showCouponInput && (
        <>
          <InputRow>
            <CouponInputWrapper>
              <CouponInput
                id={'coupon_input_field'}
                type='text'
                handleChange={onCouponInputValueChange}
                name='coupon'
                placeholder='Enter your coupon'
                value={couponCodeText}
                disabled={isLoading || Boolean(appliedCoupon)}
                error={error}
                success={Boolean(appliedCoupon)}
                colorVariant={InputColorVariant.White}
              />
            </CouponInputWrapper>

            <ApplyButton disabled={!canCouponBeApplied} onClick={onApplyClick}>
              Apply
            </ApplyButton>
          </InputRow>
          {error && <ErrorInputText>{error}</ErrorInputText>}
        </>
      )}

      {appliedCoupon && (
        <AppliedCupponTag>
          <AppliedCupponTagIcon src={greenTag} alt='tag' />
          <AppliedCupponTagText>{appliedCoupon.code}</AppliedCupponTagText>
          <AppliedCupponTagDeleteIcon src={closeIcon} alt='X' onClick={onRemoveCouponClick} />
        </AppliedCupponTag>
      )}
    </CouponWrapped>
  );
});
